import React, { useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import FieldMap from "./FieldMap";
import CropPhenologyTimeline from "../crops/CropPhenologyTimeline";
import { cropText } from "../crops/cropText";
import { buildPlatformSignUpUrl } from "../../utils/publicFarmPreview";
import cropsData from "../../data/crops.json";

/**
 * TryYourFieldExperience — the core of PRD-01.
 *
 * Drop a pin on Egypt → fetch Open-Meteo weather (keyless, direct from the
 * browser) → show a weather forecast and a per-crop ETc preview computed
 * entirely client-side: ETc = ET0 (Open-Meteo's FAO-56 reference ET) x Kc
 * (the selected crop + stage coefficient from crops.json). The multiplication
 * is shown explicitly — "evidence, not estimate".
 *
 * Satellite NDVI is deliberately a sign-up gate: it needs a server-side
 * Sentinel-2 proxy, and gating it is a sound funnel decision.
 *
 * Used full-page at /try-your-field and embedded on /platform.
 */

const FEDDAN_M2 = 4200; // 1 mm of depth over 1 feddan = 4.2 m³

const mean = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);

function formatDay(dateStr, isAr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(isAr ? "ar-EG" : "en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function withCoords(url, pin) {
  if (!pin) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}lat=${pin.lat.toFixed(5)}&lng=${pin.lng.toFixed(5)}`;
}

export default function TryYourFieldExperience({ lang = "EN", embedded = false }) {
  const isAr = lang === "AR";

  const [pin, setPin] = useState(null);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cropSlug, setCropSlug] = useState("cotton");
  const [stageIndex, setStageIndex] = useState(2);

  const crop = cropsData.crops.find((c) => c.slug === cropSlug) || cropsData.crops[0];
  const stage = crop.phenology[Math.min(stageIndex, crop.phenology.length - 1)];

  const fetchWeather = useCallback(
    async (lat, lng) => {
      setLoading(true);
      setError(null);
      setWeather(null);
      try {
        const url =
          "https://api.open-meteo.com/v1/forecast" +
          `?latitude=${lat}&longitude=${lng}` +
          "&daily=temperature_2m_max,temperature_2m_min,precipitation_sum," +
          "wind_speed_10m_max,et0_fao_evapotranspiration" +
          "&past_days=7&forecast_days=7&timezone=auto";
        const res = await fetch(url);
        if (!res.ok) throw new Error("weather fetch failed");
        const data = await res.json();
        if (!data?.daily?.time?.length) throw new Error("no weather data");
        setWeather(data);
      } catch (e) {
        setError(
          isAr
            ? "تعذّر جلب بيانات الطقس لهذا الموقع. حاول مرة أخرى."
            : "Could not load weather data for this location. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [isAr]
  );

  const handleDrop = (latlng) => {
    setPin(latlng);
    fetchWeather(latlng.lat, latlng.lng);
  };

  // Forecast slice = the 7 days from today onward (past_days=7 puts today at index 7).
  const forecast = useMemo(() => {
    if (!weather?.daily?.time) return [];
    const d = weather.daily;
    return d.time
      .map((date, i) => ({
        date,
        tMax: d.temperature_2m_max?.[i],
        tMin: d.temperature_2m_min?.[i],
        precip: d.precipitation_sum?.[i],
        wind: d.wind_speed_10m_max?.[i],
        et0: d.et0_fao_evapotranspiration?.[i],
      }))
      .slice(7, 14);
  }, [weather]);

  const etc = useMemo(() => {
    if (!forecast.length || !stage) return null;
    const et0Vals = forecast.map((f) => f.et0).filter((v) => typeof v === "number");
    if (!et0Vals.length) return null;
    const kc = Number(stage.kc);
    const series = forecast.map((f) => ({
      date: f.date,
      et0: f.et0,
      etc: typeof f.et0 === "number" ? +(f.et0 * kc).toFixed(2) : null,
    }));
    const avgEt0 = mean(et0Vals);
    const avgEtc = avgEt0 * kc;
    const totalEtc = et0Vals.reduce((s, v) => s + v, 0) * kc;
    return {
      kc,
      series,
      avgEt0: +avgEt0.toFixed(2),
      avgEtc: +avgEtc.toFixed(2),
      totalEtc: +totalEtc.toFixed(1),
      m3PerFeddan: +((totalEtc * FEDDAN_M2) / 1000).toFixed(1),
    };
  }, [forecast, stage]);

  const signUpHref = withCoords(buildPlatformSignUpUrl(), pin);

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ---- Map column ---- */}
        <div>
          <FieldMap pin={pin} onDrop={handleDrop} height={embedded ? "340px" : "440px"} />
          <p className="mt-2 text-sm text-gray-600">
            {pin
              ? `${isAr ? "موقع الحقل" : "Field location"}: ${pin.lat.toFixed(4)}°, ${pin.lng.toFixed(4)}° · ${
                  isAr ? "معاينة تغطي ~1 فدان حول هذه النقطة" : "preview covers ~1 feddan around this point"
                }`
              : isAr
              ? "انقر في أي مكان على الخريطة لإسقاط دبوس على حقلك."
              : "Click anywhere on the map to drop a pin on your field."}
          </p>
        </div>

        {/* ---- Result column ---- */}
        <div className="space-y-4">
          {!pin && (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-gray-500">
              {isAr
                ? "اختر موقع حقلك على الخريطة لرؤية الطقس واحتياج المحصول المائي فورًا — دون تسجيل."
                : "Pick your field on the map to see weather and crop water demand instantly — no sign-up."}
            </div>
          )}

          {loading && (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500">
              {isAr ? "جارٍ جلب بيانات الطقس…" : "Fetching weather data…"}
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* ETc preview — the centerpiece */}
          {weather && etc && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="text-base font-bold text-gray-900">
                {isAr ? "احتياج المحصول المائي (ETc)" : "Crop water demand (ETc)"}
              </h3>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <label className="text-xs font-medium text-gray-600">
                  <span className="block mb-1">{isAr ? "المحصول" : "Crop"}</span>
                  <select
                    value={cropSlug}
                    onChange={(e) => setCropSlug(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    {cropsData.crops.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {cropText(c.name, lang)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-medium text-gray-600">
                  <span className="block mb-1">{isAr ? "المرحلة" : "Growth stage"}</span>
                  <select
                    value={stageIndex}
                    onChange={(e) => setStageIndex(Number(e.target.value))}
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    {crop.phenology.map((s, i) => (
                      <option key={s.key} value={i}>
                        {cropText(s.name, lang)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-4 text-center">
                <div className="text-3xl font-bold text-emerald-700">
                  ≈ {etc.avgEtc} {isAr ? "مم/يوم" : "mm/day"}
                </div>
                <div className="mt-1 text-xs text-gray-600">
                  {isAr ? "ET₀ المرجعي" : "Reference ET₀"} {etc.avgEt0} {isAr ? "مم" : "mm"} ×{" "}
                  {isAr ? "معامل المحصول" : "crop coefficient"} Kc {etc.kc.toFixed(2)} (
                  {cropText(crop.name, lang)} · {cropText(stage.name, lang)}) ={" "}
                  <span className="font-semibold">ETc {etc.avgEtc} {isAr ? "مم/يوم" : "mm/day"}</span>
                </div>
              </div>

              {/* 7-day ETc series */}
              <div className="mt-4 flex gap-1.5">
                {etc.series.map((s) => {
                  const maxEtc = Math.max(...etc.series.map((x) => x.etc || 0), 1);
                  return (
                    <div key={s.date} className="flex-1 text-center">
                      <div className="h-16 flex items-end justify-center">
                        <div
                          className="w-full rounded-t bg-emerald-500"
                          style={{ height: `${((s.etc || 0) / maxEtc) * 100}%` }}
                          title={`${s.etc} mm`}
                        />
                      </div>
                      <div className="mt-1 text-[10px] text-gray-500">
                        {formatDay(s.date, isAr).split(" ")[0]}
                      </div>
                      <div className="text-[10px] font-medium text-gray-700">{s.etc}</div>
                    </div>
                  );
                })}
              </div>

              <p className="mt-3 text-xs text-gray-600">
                {isAr
                  ? `على مدى الأيام السبعة القادمة ≈ ${etc.totalEtc} مم — نحو ${etc.m3PerFeddan} م³ لكل فدان.`
                  : `Over the next 7 days ≈ ${etc.totalEtc} mm — about ${etc.m3PerFeddan} m³ for a one-feddan field.`}
              </p>
            </div>
          )}

          {/* Weather forecast strip */}
          {weather && forecast.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="text-base font-bold text-gray-900">
                {isAr ? "توقّع الطقس — 7 أيام" : "7-day weather forecast"}
              </h3>
              <div className="mt-3 grid grid-cols-7 gap-1.5">
                {forecast.map((f) => (
                  <div key={f.date} className="text-center rounded-md bg-gray-50 py-2">
                    <div className="text-[10px] text-gray-500">
                      {formatDay(f.date, isAr).split(" ")[0]}
                    </div>
                    <div className="text-xs font-semibold text-gray-800">
                      {Math.round(f.tMax)}°
                    </div>
                    <div className="text-[10px] text-gray-500">{Math.round(f.tMin)}°</div>
                    <div className="text-[10px] text-sky-600 mt-0.5">
                      {Math.round(f.precip || 0)}mm
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-gray-400">
                {isAr
                  ? "المصدر: Open-Meteo — مزوّد طقس مفتوح بلا مفتاح."
                  : "Source: Open-Meteo — open, keyless weather provider."}
              </p>
            </div>
          )}

          {/* NDVI sign-up gate */}
          {weather && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
              <div className="flex items-start gap-3">
                <span className="text-xl" aria-hidden="true">🔒</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">
                    {isAr ? "تحليل NDVI بالأقمار الصناعية" : "Satellite NDVI analysis"}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {isAr
                      ? "تحليل NDVI لهذا الحقل بالذات — للتحقق من نموذج المحصول — جزء من المنصة الكاملة. أنشئ حسابك لرؤيته."
                      : "NDVI for this exact field — used to verify the crop model — is part of the full platform. Create your account to see it."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Phenology timeline for the selected crop (full page only) */}
      {weather && !embedded && (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-base font-bold text-gray-900">
              {isAr
                ? `أين يقع ${cropText(crop.name, lang)} في موسمه`
                : `Where ${cropText(crop.name, lang)} sits in its season`}
            </h3>
            <Link to={`/crops/${crop.slug}`} className="text-sm font-medium text-emerald-700 hover:underline">
              {isAr ? "صفحة المحصول الكاملة ←" : "Full crop page →"}
            </Link>
          </div>
          <div className="mt-4">
            <CropPhenologyTimeline crop={crop} lang={lang} variant="compact" />
          </div>
        </div>
      )}

      {/* Caveats */}
      {weather && (
        <p className="mt-4 text-xs text-gray-500 leading-relaxed">
          {isAr
            ? "هذه معاينة تعتمد على بيانات عامة: ET₀ المرجعي من Open-Meteo ومعاملات محاصيل فارمتوبيا. تضيف المنصة الكاملة التحقق عبر NDVI، وتتبّع مرحلة النمو من تاريخ زراعتك، وتنبيهات الطقس، وجدولة الري."
            : "This is a preview built on public data — Open-Meteo reference ET₀ and FarmTopia's crop coefficients. The full platform adds NDVI verification, growth-stage tracking from your planting date, weather alerts, and irrigation scheduling."}
        </p>
      )}

      {/* CTAs */}
      <div className="mt-5 flex flex-wrap gap-3">
        <a
          href={signUpHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!pin}
          className={`rounded-md px-5 py-2.5 text-sm font-semibold text-white ${
            pin ? "bg-emerald-600 hover:bg-emerald-700" : "bg-emerald-300 pointer-events-none"
          }`}
        >
          {isAr ? "تابع بهذا الحقل ←" : "Continue with this field →"}
        </a>
        <Link
          to={`/wiki/${crop.slug}`}
          className="rounded-md border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          {isAr ? "كيف يُحسب هذا؟" : "See how this is computed"}
        </Link>
        <Link
          to="/enterprises"
          className="rounded-md border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          {isAr ? "أدير عدة مزارع" : "I manage multiple farms"}
        </Link>
        {embedded && (
          <Link
            to="/try-your-field"
            className="rounded-md px-5 py-2.5 text-sm font-semibold text-emerald-700 hover:underline"
          >
            {isAr ? "افتح المعاينة الكاملة ←" : "Open the full field preview →"}
          </Link>
        )}
      </div>
    </div>
  );
}
